import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FiCopy, FiPlus, FiTrash2, FiAlertTriangle, FiEdit2, FiSave } from 'react-icons/fi';
import BASE_URL from '../../../config/config';
import { cabecerasAuth } from '../../../queries/cliente';
import FilaAjuste from '../../../components/ui/FilaAjuste';

/**
 * Panel de integraciones con IA: gestiona los tokens con los que un asistente
 * (ChatGPT, Claude, Cursor…) se conecta al servidor MCP de Soporte Adhoc.
 *
 * Cada conexión lleva sus propios permisos por módulo y operación. El catálogo
 * lo sirve el backend ya marcado con lo que el rol de la persona alcanza, así
 * que aquí no se replica la política: lo que llega deshabilitado, se pinta
 * deshabilitado.
 *
 * El token en claro solo existe en el momento de crearlo: se enseña una vez y
 * después la API únicamente devuelve su prefijo. Por eso la tarjeta del token
 * recién creado se queda en pantalla hasta que la persona la cierra.
 */
const IntegracionesIA = ({ onAviso }) => {
    const { t } = useTranslation();
    const [modulos, setModulos] = useState([]);
    const [conexiones, setConexiones] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [nombre, setNombre] = useState('');
    const [permisos, setPermisos] = useState({});
    const [creando, setCreando] = useState(false);
    const [tokenNuevo, setTokenNuevo] = useState(null);
    const [copiado, setCopiado] = useState('');
    const [porRevocar, setPorRevocar] = useState(null);
    const [editando, setEditando] = useState(null);
    const [permisosEdicion, setPermisosEdicion] = useState({});

    const urlMcp = useMemo(() => `${BASE_URL.replace(/\/+$/, '')}/mcp`, []);
    const urlConToken = tokenNuevo ? `${urlMcp}/${tokenNuevo.token}` : '';

    const cargar = async () => {
        try {
            setCargando(true);
            const [catalogo, tokens] = await Promise.all([
                axios.get(`${BASE_URL}/api/mcp/modulos`, cabecerasAuth()),
                axios.get(`${BASE_URL}/api/mcp/tokens`, cabecerasAuth()),
            ]);
            setModulos(catalogo.data.modulos || []);
            setConexiones(tokens.data.tokens || []);
            // Arranque conservador: lectura de todo lo que el rol alcance
            setPermisos(Object.fromEntries((catalogo.data.modulos || [])
                .filter((m) => m.operaciones.some((o) => o.operacion === 'leer' && o.disponible))
                .map((m) => [m.modulo, ['leer']])));
        } catch (error) {
            onAviso?.('error', t('integraciones.errorCargar'));
        } finally {
            setCargando(false);
        }
    };

    // Una sola carga al montar: la lista solo cambia por acciones de esta pantalla
    useEffect(() => { cargar(); }, []);

    const alternar = (estado, aplicar, modulo, operacion) => {
        const actuales = estado[modulo] || [];
        const siguientes = actuales.includes(operacion)
            ? actuales.filter((o) => o !== operacion)
            : [...actuales, operacion];
        const copia = { ...estado };
        if (siguientes.length) copia[modulo] = siguientes;
        else delete copia[modulo];
        aplicar(copia);
    };

    const totalPermisos = (mapa) => Object.values(mapa).reduce((suma, ops) => suma + ops.length, 0);

    const copiar = async (texto, clave) => {
        try {
            await navigator.clipboard.writeText(texto);
            setCopiado(clave);
            setTimeout(() => setCopiado(''), 2000);
        } catch (error) {
            onAviso?.('error', t('integraciones.errorCopiar'));
        }
    };

    const crear = async () => {
        try {
            setCreando(true);
            const respuesta = await axios.post(
                `${BASE_URL}/api/mcp/tokens`,
                { nombre: nombre.trim() || t('integraciones.nombrePorDefecto'), permisos },
                cabecerasAuth(),
            );
            setTokenNuevo(respuesta.data);
            setNombre('');
            await cargar();
        } catch (error) {
            onAviso?.('error', error.response?.data?.message || t('integraciones.errorCrear'));
        } finally {
            setCreando(false);
        }
    };

    const guardarPermisos = async (conexion) => {
        try {
            await axios.patch(`${BASE_URL}/api/mcp/tokens/${conexion.id}`, { permisos: permisosEdicion }, cabecerasAuth());
            setEditando(null);
            await cargar();
            onAviso?.('success', t('integraciones.permisosGuardados'));
        } catch (error) {
            onAviso?.('error', error.response?.data?.message || t('integraciones.errorGuardarPermisos'));
        }
    };

    const revocar = async (conexion) => {
        try {
            await axios.delete(`${BASE_URL}/api/mcp/tokens/${conexion.id}`, cabecerasAuth());
            setPorRevocar(null);
            if (tokenNuevo?.conexion?.id === conexion.id) setTokenNuevo(null);
            await cargar();
            onAviso?.('success', t('integraciones.revocada'));
        } catch (error) {
            onAviso?.('error', t('integraciones.errorRevocar'));
        }
    };

    const fecha = (valor) => (valor
        ? new Date(valor).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : t('integraciones.nuncaUsada'));

    /** Matriz módulo × operación, compartida por el alta y la edición. */
    const Matriz = ({ estado, aplicar, idPrefijo }) => (
        <div className="mcp-matriz" role="group" aria-label={t('integraciones.permisos')}>
            <div className="mcp-matriz-cabecera">
                <span>{t('integraciones.seccion')}</span>
                <span>{t('integraciones.opLeer')}</span>
                <span>{t('integraciones.opCrear')}</span>
                <span>{t('integraciones.opEditar')}</span>
                <span>{t('integraciones.opEliminar')}</span>
            </div>
            {modulos.map((modulo) => (
                <div className="mcp-matriz-fila" key={modulo.modulo}>
                    <span className="mcp-matriz-nombre">{t(`integraciones.modulos.${modulo.modulo}`, modulo.etiqueta)}</span>
                    {['leer', 'crear', 'editar', 'eliminar'].map((operacion) => {
                        const definicion = modulo.operaciones.find((o) => o.operacion === operacion);
                        if (!definicion) return <span key={operacion} className="mcp-celda-vacia" aria-hidden="true">—</span>;
                        const marcado = (estado[modulo.modulo] || []).includes(operacion);
                        return (
                            <label
                                key={operacion}
                                className={`mcp-celda ${definicion.disponible ? '' : 'no-disponible'} ${operacion === 'eliminar' ? 'peligro' : ''}`}
                                title={definicion.disponible ? '' : t('integraciones.noDisponibleRol')}
                            >
                                <input
                                    type="checkbox"
                                    id={`${idPrefijo}-${modulo.modulo}-${operacion}`}
                                    checked={marcado}
                                    disabled={!definicion.disponible}
                                    onChange={() => alternar(estado, aplicar, modulo.modulo, operacion)}
                                />
                            </label>
                        );
                    })}
                </div>
            ))}
        </div>
    );

    // Una línea por sección con permisos: se lee mejor que una ristra separada por puntos
    const resumenPermisos = (mapa) => Object.entries(mapa || {})
        .filter(([, ops]) => ops?.length)
        .map(([modulo, ops]) => ({ modulo, texto: `${t(`integraciones.modulos.${modulo}`, modulo)}: ${ops.map((op) => t(`integraciones.op${op.charAt(0).toUpperCase()}${op.slice(1)}`, op).toLowerCase()).join(', ')}` }));

    return (
        <div className="mcp-panel">
            <div className="mcp-intro">
                <p>{t('integraciones.explicacion')}</p>
                <p className="mcp-aviso">
                    <FiAlertTriangle aria-hidden="true" />
                    <span>{t('integraciones.avisoSeguridad')}</span>
                </p>
            </div>

            <FilaAjuste titulo={t('integraciones.conexionesActivas')} texto={t('integraciones.conexionesTexto')}>
                {cargando ? (
                    <p className="campo-ayuda">{t('integraciones.cargando')}</p>
                ) : conexiones.length === 0 ? (
                    <p className="mcp-vacio">{t('integraciones.sinConexiones')}</p>
                ) : (
                    <ul className="mcp-conexiones">
                        {conexiones.map((conexion) => {
                            const permisosConexion = resumenPermisos(conexion.permisos);
                            return (
                                <li key={conexion.id} className="mcp-conexion">
                                    <div className="mcp-conexion-fila">
                                        <div className="mcp-conexion-datos">
                                            <strong>{conexion.nombre}</strong>
                                            <span>
                                                <code>{conexion.prefijo}…</code>
                                                {' '}{t('integraciones.ultimoUsoFrase', { cuando: fecha(conexion.ultimo_uso) })}
                                            </span>
                                        </div>
                                        {porRevocar === conexion.id ? (
                                            <div className="mcp-confirmar">
                                                <span>{t('integraciones.confirmarRevocar')}</span>
                                                <button type="button" className="peligro" onClick={() => revocar(conexion)}>
                                                    {t('integraciones.revocar')}
                                                </button>
                                                <button type="button" onClick={() => setPorRevocar(null)}>{t('comun.cancelar')}</button>
                                            </div>
                                        ) : (
                                            <div className="mcp-conexion-acciones">
                                                <button
                                                    type="button"
                                                    aria-expanded={editando === conexion.id}
                                                    onClick={() => {
                                                        setEditando(editando === conexion.id ? null : conexion.id);
                                                        setPermisosEdicion(conexion.permisos || {});
                                                    }}
                                                >
                                                    <FiEdit2 aria-hidden="true" /> {t('integraciones.editarPermisos')}
                                                </button>
                                                <button type="button" className="mcp-revocar" onClick={() => setPorRevocar(conexion.id)}>
                                                    <FiTrash2 aria-hidden="true" /> {t('integraciones.revocar')}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {editando === conexion.id ? (
                                        <div className="mcp-edicion">
                                            <Matriz estado={permisosEdicion} aplicar={setPermisosEdicion} idPrefijo={`edicion-${conexion.id}`} />
                                            <div className="mcp-edicion-acciones">
                                                <button
                                                    type="button"
                                                    className="boton-primario"
                                                    onClick={() => guardarPermisos(conexion)}
                                                    disabled={totalPermisos(permisosEdicion) === 0}
                                                >
                                                    <FiSave aria-hidden="true" /> {t('comun.guardar')}
                                                </button>
                                                <button type="button" onClick={() => setEditando(null)}>{t('comun.cancelar')}</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <ul className="mcp-conexion-permisos">
                                            {permisosConexion.length
                                                ? permisosConexion.map((p) => <li key={p.modulo}>{p.texto}</li>)
                                                : <li>{t('integraciones.sinPermisos')}</li>}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </FilaAjuste>

            <FilaAjuste titulo={t('integraciones.nombreConexion')} texto={t('integraciones.nuevaTexto')}>
                <div className="campo-ajuste">
                    <label htmlFor="mcp-nombre">{t('integraciones.nombreEtiqueta')}</label>
                    <input
                        id="mcp-nombre"
                        value={nombre}
                        maxLength={80}
                        placeholder={t('integraciones.nombrePlaceholder')}
                        onChange={(e) => setNombre(e.target.value)}
                        aria-describedby="mcp-nombre-ayuda"
                    />
                    <p className="campo-ayuda" id="mcp-nombre-ayuda">{t('integraciones.nombreAyuda')}</p>
                </div>

                <div className="campo-ajuste">
                    <span className="mcp-etiqueta" id="mcp-permisos-titulo">{t('integraciones.permisos')}</span>
                    <p className="campo-ayuda">{t('integraciones.permisosAyuda')}</p>
                    {!cargando && <Matriz estado={permisos} aplicar={setPermisos} idPrefijo="nuevo" />}
                </div>

                <button type="button" className="boton-primario mcp-crear-boton" onClick={crear} disabled={creando || totalPermisos(permisos) === 0}>
                    <FiPlus aria-hidden="true" /> {creando ? t('integraciones.creando') : t('integraciones.crear')}
                </button>

                {tokenNuevo && (
                    <div className="mcp-token-nuevo" role="alert">
                        <strong>{t('integraciones.tokenCreado')}</strong>
                        <p className="mcp-token-aviso">{t('integraciones.tokenSoloUnaVez')}</p>

                        <span className="mcp-etiqueta">{t('integraciones.urlConector')}</span>
                        <div className="mcp-copiable">
                            <code>{urlConToken}</code>
                            <button type="button" onClick={() => copiar(urlConToken, 'url')}>
                                <FiCopy aria-hidden="true" /> {copiado === 'url' ? t('integraciones.copiado') : t('integraciones.copiar')}
                            </button>
                        </div>

                        <span className="mcp-etiqueta">{t('integraciones.tokenSuelto')}</span>
                        <div className="mcp-copiable">
                            <code>{tokenNuevo.token}</code>
                            <button type="button" onClick={() => copiar(tokenNuevo.token, 'token')}>
                                <FiCopy aria-hidden="true" /> {copiado === 'token' ? t('integraciones.copiado') : t('integraciones.copiar')}
                            </button>
                        </div>

                        <button type="button" className="mcp-cerrar" onClick={() => setTokenNuevo(null)}>
                            {t('integraciones.hecho')}
                        </button>
                    </div>
                )}
            </FilaAjuste>

            <FilaAjuste titulo={t('integraciones.comoConectar')} texto={t('integraciones.comoConectarTexto')}>
                <div className="mcp-paso">
                    <h4>ChatGPT</h4>
                    <p>{t('integraciones.pasosChatgpt')}</p>
                    <div className="mcp-copiable">
                        <code>{urlMcp}/{t('integraciones.tuToken')}</code>
                        <button type="button" onClick={() => copiar(urlMcp, 'base')}>
                            <FiCopy aria-hidden="true" /> {copiado === 'base' ? t('integraciones.copiado') : t('integraciones.copiarBase')}
                        </button>
                    </div>
                </div>

                <div className="mcp-paso">
                    <h4>{t('integraciones.otrosClientes')}</h4>
                    <p>{t('integraciones.pasosOtros')}</p>
                    <pre className="mcp-json">{JSON.stringify({
                        mcpServers: {
                            'soporte-adhoc': {
                                type: 'http',
                                url: urlMcp,
                                headers: { Authorization: `Bearer ${t('integraciones.tuToken')}` },
                            },
                        },
                    }, null, 2)}</pre>
                </div>
            </FilaAjuste>
        </div>
    );
};

export default IntegracionesIA;
